// Dashboard: die Lage-Sätze der Zonen und was eine Geräte-Karte zeigt.
// Rein gerechnet, ohne Stores -- die Seite reicht die Zahlen herein
// (Tests: lib/dashboard.test.mjs).
//
// Lage-Satz (docs/gestaltung.md, Regel 3): die Ausnahme vorn mit ihrem
// Ton, der Normalfall ein Wort. Farbe trägt nur, was ein Befund ist --
// eine offene Badtür ist keiner.
import type { REIHE } from "../farbe/farbe";
import type { LageTeil } from "../lage";

/** „A", „A und B", „A, B und 2 weitere". */
export function aufzaehlung(namen: string[]): string {
  if (namen.length <= 2) return namen.join(" und ");
  return `${namen.slice(0, 2).join(", ")} und ${namen.length - 2} weitere`;
}

export interface Tuer {
  name: string;
  offen: boolean;
  /** Außentür: offen ist ein Handgriff, nicht bloß eine Auskunft. */
  wichtig: boolean;
  /** Außentür, die länger offen steht als erlaubt: kein Handgriff mehr,
   *  sondern ein Befund (Tim 04.10.2026: „Eine lange geöffnete Front ist
   *  nicht normal"). Dann meldet auch das Backend mit Push. */
  zuLang?: boolean;
}

/** Steht die Tür länger offen als erlaubt? Gezählt ab der Flanke „auf"
 *  (`since`), nie ab der letzten Funkmeldung. Ohne Flanke oder Schwelle:
 *  nein. */
export function offenZuLang(offen: boolean, since: string | undefined, schwelleS: number | undefined, jetztMs: number): boolean {
  if (!offen || !since || !schwelleS || schwelleS <= 0) return false;
  const seit = Date.parse(since);
  return Number.isFinite(seit) && jetztMs - seit >= schwelleS * 1000;
}

/** Zustand einer Tür fürs Schild: zu = ok, offen = Auskunft (neutral),
 *  eine offene Außentür = Handgriff (gelb), zu lange offen = rot. */
export function tuerTon(t: Tuer): "ok" | "neutral" | "warn" | "danger" {
  if (!t.offen) return "ok";
  if (!t.wichtig) return "neutral";
  return t.zuLang ? "danger" : "warn";
}

export function zuhauseLage(lichterAn: number, lichterGesamt: number, tueren: Tuer[]): LageTeil[] {
  const teile: LageTeil[] = [];
  const wichtigOffen = tueren.filter((t) => t.offen && t.wichtig);
  const sonstOffen = tueren.filter((t) => t.offen && !t.wichtig).length;
  const zuLang = wichtigOffen.filter((t) => t.zuLang);
  const kurz = wichtigOffen.filter((t) => !t.zuLang);
  if (zuLang.length) {
    teile.push({ text: `${aufzaehlung(zuLang.map((t) => t.name))} steht zu lange offen`, ton: "danger" });
  }
  if (kurz.length) {
    teile.push({ text: `${aufzaehlung(kurz.map((t) => t.name))} offen`, ton: "warn" });
  }
  if (lichterGesamt > 0) {
    teile.push({ text: lichterAn === 0 ? "Licht aus" : lichterAn === 1 ? "1 Licht an" : `${lichterAn} Lichter an` });
  }
  if (sonstOffen > 0) {
    teile.push({ text: sonstOffen === 1 ? "1 Tür offen" : `${sonstOffen} Türen offen` });
  } else if (tueren.length > 0 && wichtigOffen.length === 0) {
    teile.push({ text: "Türen zu", ton: "ok" });
  }
  return teile;
}

export interface Auffaellig {
  name: string;
  kritisch: boolean;
}

/** Energie & Klima: auffällige Sensoren beim Namen, dazu die
 *  Netzfrequenz, wenn sie ausschlägt. */
export function klimaLage(auffaellig: Auffaellig[], netz: string | undefined): LageTeil[] {
  const teile: LageTeil[] = [];
  const krit = auffaellig.filter((a) => a.kritisch).map((a) => a.name);
  const auff = auffaellig.filter((a) => !a.kritisch).map((a) => a.name);
  if (krit.length) teile.push({ text: `kritisch: ${aufzaehlung(krit)}`, ton: "danger" });
  if (netz === "problem") teile.push({ text: "Netzfrequenz kritisch", ton: "danger" });
  if (auff.length) teile.push({ text: `auffällig: ${aufzaehlung(auff)}`, ton: "warn" });
  if (netz === "warning") teile.push({ text: "Netzfrequenz auffällig", ton: "warn" });
  if (teile.length === 0) teile.push({ text: "alles im Rahmen", ton: "ok" });
  return teile;
}

/** Diagnose-Zähler (Laufzeit, WLAN-Signal, Funkgüte): gehören nicht von
 *  selbst auf eine Karte -- wählbar bleiben sie im Zahnrad. Dieselbe
 *  Regel wie im Verlauf (history.SkipNoise im Backend). */
export function istDiagnose(slug: string, unit: string): boolean {
  if (["s", "h", "dB", "dBm"].includes(unit)) return true;
  return slug.includes("wifi_signal") || slug.includes("uptime") || slug.includes("linkquality");
}

/** Laufzeit: 554284 s -> "6 d 9 h". Dieselbe Stufung und dieselben
 *  Kürzel wie formatUptime (lib/format.ts). */
function laufzeit(sekunden: number): string {
  const min = Math.floor(sekunden / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h`;
  const rest = h % 24;
  return rest > 0 ? `${Math.floor(h / 24)} d ${rest} h` : `${Math.floor(h / 24)} d`;
}

/** Messwert einer Entität, wie er auf der Karte steht: deutsches Komma,
 *  Sekunden als Dauer. Was keine Zahl ist („on", „offen"), bleibt. */
export function messwert(state: string, unit: string): { wert: string; einheit: string } {
  if (!/^-?\d+(\.\d+)?$/.test(state)) return { wert: state, einheit: unit };
  const n = Number(state);
  if (unit === "s") return { wert: laufzeit(n), einheit: "" };
  // Höchstens zwei Nachkommastellen: 8.163 #/cm³ ist auf einer Karte
  // nicht genauer als 8,16.
  const stellen = Math.min(2, (state.split(".")[1] ?? "").length);
  return { wert: n.toLocaleString("de-DE", { minimumFractionDigits: stellen, maximumFractionDigits: stellen }), einheit: unit };
}

/** Messgröße einer Entität -- sie bestimmt die Farbe ihrer Kurve
 *  (REIHE in lib/farbe/farbe.ts): die Farbe folgt der Größe, nicht der
 *  Karte. Feinstaub und VOC zählen zur Luftqualität. */
export function messgroesse(slug: string): keyof typeof REIHE {
  const s = slug.toLowerCase();
  if (s.includes("temp")) return "temperatur";
  if (s.includes("hum")) return "feuchte";
  if (s.includes("pressure")) return "druck";
  if (s.includes("co2")) return "co2";
  if (s.includes("iaq") || s.includes("voc") || s.startsWith("pm") || s.startsWith("partikel")) return "iaq";
  return "leistung";
}
